import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { assignDevice, deleteDevice, unassignDevice } from "../api/devices";
import { listUsers } from "../api/users";
import useDeviceList from "../hooks/useDeviceList";
import { consoleFeatures } from "../config/consoleFeatures";

export default function Devices() {
  const { profile } = useOutletContext();
  const { devices, loading, error: listError, refresh: load } = useDeviceList();
  const [installers, setInstallers] = useState([]);
  const [selections, setSelections] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyBeid, setBusyBeid] = useState(null);
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    let active = true;
    if (isAdmin) listUsers().then((users) => {
      if (active) setInstallers(users.filter((user) => user.role === "tech" && user.is_active));
    }).catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [isAdmin]);

  const doAssign = async (device) => {
    const userId = selections[device.beid];
    if (!userId) { setError("Select an installer first."); return; }
    setBusyBeid(device.beid); setError(""); setNotice("");
    try {
      await assignDevice(device.beid, userId);
      setNotice(`${device.beid} assigned.`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusyBeid(null); }
  };

  const doUnassign = async (device, installer) => {
    setBusyBeid(device.beid); setError(""); setNotice("");
    try {
      await unassignDevice(device.beid, installer.id);
      setNotice(`${installer.full_name || installer.email} removed from ${device.beid}.`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusyBeid(null); }
  };

  const assignedUsers = (device) => device.assigned_users || (
    device.assigned_user_id
      ? [{ id: device.assigned_user_id, email: device.assigned_user_email }]
      : []
  );

  const doDelete = async (device) => {
    if (!window.confirm(`Delete ${device.beid}? The edge may register it again later.`)) return;
    setBusyBeid(device.beid); setError("");
    try {
      await deleteDevice(device.id);
      setNotice(`${device.beid} deleted.`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusyBeid(null); }
  };

  return <div>
    <h2 className="page-title">Devices</h2>
    <p className="page-sub">Edge units appear here automatically after certificate-authenticated registration.</p>
    {(error || listError) && <div className="alert alert-error">{error || listError}</div>}
    {notice && <div className="alert alert-success">{notice}</div>}
    {loading ? <div className="spinner" /> : devices.length === 0 ? (
      <div className="card"><div className="empty">No edge units are currently registered.</div></div>
    ) : <div className="card"><div className="table-wrap"><table className="tbl">
      <thead><tr><th>BEID</th>{consoleFeatures.showDeviceName && <th>Name</th>}<th>State</th><th>Installer</th><th></th></tr></thead>
      <tbody>{devices.map((device) => <tr key={device.id}>
        <td className="mono">{device.beid}</td>
        {consoleFeatures.showDeviceName && <td>{device.name || <span className="muted">—</span>}</td>}
        <td><span className={`badge ${device.enrollment_state === "assigned" ? "badge-green" : "badge-amber"}`}>{device.enrollment_state}</span></td>
        <td>
          {assignedUsers(device).length === 0 ? <span className="muted">Unassigned</span> : assignedUsers(device).map((user) => (
            <div key={user.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span>{user.full_name || user.email}</span>
              {isAdmin && <button className="btn btn-outline btn-sm" disabled={busyBeid === device.beid} onClick={() => doUnassign(device, user)}>Remove</button>}
            </div>
          ))}
          {isAdmin && <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select className="input" value={selections[device.beid] || ""} onChange={(event) => setSelections((current) => ({ ...current, [device.beid]: event.target.value }))}>
              <option value="">Select technician</option>
              {installers.filter((user) => !assignedUsers(device).some((assigned) => assigned.id === user.id)).map((user) => <option key={user.id} value={user.id}>{user.full_name || user.email}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" disabled={busyBeid === device.beid || !selections[device.beid]} onClick={() => doAssign(device)}>Add</button>
          </div>}
        </td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {isAdmin && <>
            <button className="btn btn-outline btn-sm" disabled={busyBeid === device.beid} onClick={() => doDelete(device)} style={{ marginRight: 8 }}>Delete</button>
          </>}
          <Link to={`/devices/${device.id}`} className="btn btn-outline btn-sm">Details</Link>
        </td>
      </tr>)}</tbody>
    </table></div></div>}
  </div>;
}
